import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Chip, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const Materials = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentMaterial, setCurrentMaterial] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: '',
    unit: '',
    stockQuantity: 0,
    minimumStock: 0,
    unitPrice: 0,
    supplier: '',
    description: '',
    location: '',
    imageUrl: ''
  });

  // Fetch materials and projects on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch materials
        const materialsResponse = await axios.get('/api/materials', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Fetch projects
        const projectsResponse = await axios.get('/api/projects', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const materialsData = materialsResponse.data.materials || [];
        setMaterials(materialsData);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(materialsData.map(material => material.category))];
        setCategories(uniqueCategories);
        
        setProjects(projectsResponse.data.projects || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        enqueueSnackbar('Gagal memuat data material', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token, enqueueSnackbar]);

  // Handle dialog open for create
  const handleCreateMaterial = () => {
    setDialogMode('create');
    setFormData({
      name: '',
      code: '',
      category: '',
      unit: '',
      stockQuantity: 0,
      minimumStock: 0,
      unitPrice: 0,
      supplier: '',
      description: '',
      location: '',
      imageUrl: ''
    });
    setOpenDialog(true);
  };

  // Handle dialog open for edit
  const handleEditMaterial = (material) => {
    setDialogMode('edit');
    setCurrentMaterial(material);
    setFormData({
      name: material.name,
      code: material.code || '',
      category: material.category,
      unit: material.unit,
      stockQuantity: material.stockQuantity,
      minimumStock: material.minimumStock || 0,
      unitPrice: material.unitPrice || 0,
      supplier: material.supplier || '',
      description: material.description || '',
      location: material.location || '',
      imageUrl: material.imageUrl || ''
    });
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentMaterial(null);
  };

  // Handle form field change
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        // Create material
        const response = await axios.post('/api/materials', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setMaterials((prevMaterials) => [...prevMaterials, response.data]);
        
        // Update categories if new category
        if (!categories.includes(response.data.category)) {
          setCategories((prevCategories) => [...prevCategories, response.data.category]);
        }
        
        enqueueSnackbar('Material berhasil dibuat', { variant: 'success' });
      } else {
        // Update material
        const response = await axios.put(`/api/materials/${currentMaterial.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setMaterials((prevMaterials) => 
          prevMaterials.map((material) => (material.id === currentMaterial.id ? response.data : material))
        );
        
        // Update categories if new category
        if (!categories.includes(response.data.category)) {
          setCategories((prevCategories) => [...prevCategories, response.data.category]);
        }
        
        enqueueSnackbar('Material berhasil diperbarui', { variant: 'success' });
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error submitting material:', error);
      enqueueSnackbar('Gagal menyimpan material', { variant: 'error' });
    }
  };

  // Handle material deletion
  const handleDeleteMaterial = async (materialId) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus material ini?')) {
      try {
        await axios.delete(`/api/materials/${materialId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setMaterials((prevMaterials) => prevMaterials.filter((material) => material.id !== materialId));
        enqueueSnackbar('Material berhasil dihapus', { variant: 'success' });
      } catch (error) {
        console.error('Error deleting material:', error);
        enqueueSnackbar('Gagal menghapus material', { variant: 'error' });
      }
    }
  };

  // Filter materials based on category and search query
  const filteredMaterials = materials.filter((material) => {
    // Filter by category
    if (filterCategory && material.category !== filterCategory) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        material.name.toLowerCase().includes(query) ||
        (material.code && material.code.toLowerCase().includes(query)) ||
        (material.description && material.description.toLowerCase().includes(query)) ||
        (material.supplier && material.supplier.toLowerCase().includes(query))
      );
    }
    
    return true;
  });

  // DataGrid columns for table view
  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'code', headerName: 'Kode', width: 120 },
    { field: 'name', headerName: 'Nama Material', flex: 1 },
    { field: 'category', headerName: 'Kategori', width: 150 },
    { field: 'unit', headerName: 'Satuan', width: 100 },
    { 
      field: 'stockQuantity', 
      headerName: 'Stok', 
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {params.value}
          {params.row.minimumStock && params.value <= params.row.minimumStock && (
            <Tooltip title="Stok Rendah">
              <WarningIcon color="warning" fontSize="small" sx={{ ml: 1 }} />
            </Tooltip>
          )}
        </Box>
      )
    },
    { 
      field: 'unitPrice', 
      headerName: 'Harga Satuan', 
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return 'Rp 0';
        return `Rp ${params.value.toLocaleString('id-ID')}`;
      }
    },
    { field: 'supplier', headerName: 'Supplier', width: 180 },
    { field: 'location', headerName: 'Lokasi', width: 150 },
    {
      field: 'actions',
      headerName: 'Aksi',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditMaterial(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Hapus">
            <IconButton
              size="small"
              onClick={() => handleDeleteMaterial(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Manajemen Material
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Kelola kebutuhan dan distribusi material untuk proyek-proyek konstruksi prefabrikasi.
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Cari Material"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Kategori</InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                label="Kategori"
              >
                <MenuItem value="">Semua Kategori</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}
              >
                {viewMode === 'grid' ? 'Tampilan Tabel' : 'Tampilan Grid'}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FilterListIcon />}
                onClick={() => {
                  setFilterCategory('');
                  setSearchQuery('');
                }}
              >
                Reset Filter
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateMaterial}
              >
                Tambah Material
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      ) : viewMode === 'grid' ? (
        // Grid view
        <Grid container spacing={3}>
          {filteredMaterials.map((material) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={material.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardMedia
                  component="img"
                  height="140"
                  image={material.imageUrl || 'https://via.placeholder.com/300x140?text=Material'}
                  alt={material.name}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h6" component="div" noWrap>
                    {material.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {material.code || '-'}
                  </Typography>
                  <Chip 
                    label={material.category} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                    sx={{ mb: 1 }} 
                  />
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Stok:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {material.stockQuantity} {material.unit}
                          {material.minimumStock && material.stockQuantity <= material.minimumStock && (
                            <Tooltip title="Stok Rendah">
                              <WarningIcon color="warning" fontSize="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                            </Tooltip>
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Harga:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {material.unitPrice ? `Rp ${material.unitPrice.toLocaleString('id-ID')}` : 'Rp 0'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
                <Divider />
                <CardActions>
                  <Button 
                    size="small" 
                    startIcon={<EditIcon />}
                    onClick={() => handleEditMaterial(material)}
                  >
                    Edit
                  </Button>
                  <Button 
                    size="small" 
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteMaterial(material.id)}
                  >
                    Hapus
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // Table view
        <Paper sx={{ width: '100%', height: 500 }}>
          <DataGrid
            rows={filteredMaterials}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            density="standard"
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </Paper>
      )}
      
      {/* Create/Edit Material Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Tambah Material Baru' : 'Edit Material'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nama Material"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Kode Material"
                name="code"
                value={formData.code}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Kategori"
                name="category"
                value={formData.category}
                onChange={handleFormChange}
                required
                select={categories.length > 0}
              >
                {categories.length > 0 ? (
                  categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))
                ) : null}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Satuan"
                name="unit"
                value={formData.unit}
                onChange={handleFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Jumlah Stok"
                name="stockQuantity"
                type="number"
                value={formData.stockQuantity}
                onChange={handleFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Stok Minimum"
                name="minimumStock"
                type="number"
                value={formData.minimumStock}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Harga Satuan"
                name="unitPrice"
                type="number"
                value={formData.unitPrice}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Supplier"
                name="supplier"
                value={formData.supplier}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Lokasi"
                name="location"
                value={formData.location}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL Gambar"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Deskripsi"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {dialogMode === 'create' ? 'Tambah' : 'Simpan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Materials;
